// ============================================================================
// LandingPage — the public marketing front page (route "/", namespace "landing").
// ----------------------------------------------------------------------------
// A bold, energetic Pulse marketing page that sells ViccoFitness to personal
// trainers. Composition lives in app/landing/*; this file orchestrates the
// sections, the page-load reveal, and the optional-first session wiring.
//
// • DESIGN — built entirely on the LOCKED Pulse design system (Button, Card,
//   Badge, StatTile, icons, LanguageToggle + the tokens). Nothing in the design
//   system is restyled; this page only composes it.
// • MOBILE-FIRST — every section is designed at 390 first and scales up.
// • MOTION — Framer Motion throughout (a custom self-animating hero graphic,
//   staggered reveals, count-ups, a sweeping CTA sheen), all reduced-motion
//   aware via useReducedMotion + the global prefers-reduced-motion reset.
// • API / live-sync — wired through the typed API client + React Query via the
//   optional-first useReturningTrainer() probe (shares qk.auth.me with the auth
//   layer & AppShell live-sync). The page is 100% functional with no session.
//
// Behaviour preserved from the scaffold: a fully-authenticated visitor is sent
// straight into the app; everyone else sees the marketing page with a clear
// trainer LOGIN entry that routes to /login.
// ============================================================================
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { LandingNav } from "./landing/LandingNav";
import { LandingHero } from "./landing/LandingHero";
import { ValueProps } from "./landing/ValueProps";
import { FeatureHighlights } from "./landing/FeatureHighlights";
import { ClientPortalShowcase } from "./landing/ClientPortalShowcase";
import { LandingCta } from "./landing/LandingCta";
import { LandingFooter } from "./landing/LandingFooter";
import { useReturningTrainer } from "./landing/useReturningTrainer";

export function LandingPage() {
  // Bind this page to its own i18n namespace (keeps `t` reading landing.* while
  // still falling back to common.* for shared keys like app.name).
  const { ready } = useTranslation("landing");
  const { isAuthenticated, isLoading } = useAuth();
  const session = useReturningTrainer();

  // A confirmed, logged-in trainer skips the marketing page entirely.
  if (!isLoading && isAuthenticated) return <Navigate to="/app" replace />;

  // Avoid a flash of untranslated keys before resources settle.
  if (!ready) return <div className="min-h-screen bg-paper" aria-hidden />;

  return (
    <div className="min-h-screen bg-paper">
      <LandingNav />
      <main>
        <LandingHero session={session} />
        <ValueProps />
        <FeatureHighlights />
        <ClientPortalShowcase />
        <LandingCta session={session} />
      </main>
      <LandingFooter />
    </div>
  );
}
